import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type SideRailView = 'chat' | 'history';

interface SideRailContextValue {
  isOpen: boolean;
  initialPrompt: string;
  sideRailView: SideRailView;
  activeAskHobbesChatId: string | null;
  openSideRail: (options?: string | { prompt?: string; view?: SideRailView }) => void;
  closeSideRail: () => void;
  toggleSideRail: () => void;
  setInitialPrompt: (prompt: string) => void;
  setSideRailView: (view: SideRailView) => void;
  setActiveAskHobbesChatId: (chatId: string | null) => void;
}

const SideRailContext = createContext<SideRailContextValue | undefined>(undefined);

interface SideRailProviderProps {
  children: ReactNode;
}

export function SideRailProvider({ children }: SideRailProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState('');
  const [sideRailView, setSideRailView] = useState<SideRailView>('chat');
  const [activeAskHobbesChatId, setActiveAskHobbesChatId] = useState<string | null>(null);

  const openSideRail = useCallback((options?: string | { prompt?: string; view?: SideRailView }) => {
    const prompt = typeof options === 'string' ? options : options?.prompt;
    const view = typeof options === 'string' ? 'chat' : options?.view ?? 'chat';
    if (prompt) {
      setInitialPrompt(prompt);
    }
    setSideRailView(view);
    setIsOpen(true);
  }, []);

  const closeSideRail = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleSideRail = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      if (next) {
        setSideRailView('chat');
      }
      return next;
    });
  }, []);

  const value: SideRailContextValue = {
    isOpen,
    initialPrompt,
    sideRailView,
    activeAskHobbesChatId,
    openSideRail,
    closeSideRail,
    toggleSideRail,
    setInitialPrompt,
    setSideRailView,
    setActiveAskHobbesChatId,
  };

  return (
    <SideRailContext.Provider value={value}>
      {children}
    </SideRailContext.Provider>
  );
}

export function useSideRail(): SideRailContextValue {
  const context = useContext(SideRailContext);
  if (!context) {
    throw new Error('useSideRail must be used within a SideRailProvider');
  }
  return context;
}

export { SideRailContext };
